import React from "react";
import { Formik, Form } from "formik";
import { Grid } from "@material-ui/core";

import {
  ButtonsWrapper,
  CancelButton,
  SaveButton
} from "../../../../../elements/Button";
import Section from "../../../../../elements/Section";
import SectionHeader from "../../../../../elements/SectionHeader";
import I18nText from "../../../../../elements/I18nText";
import PermissionCheckbox from "./PermissionCheckbox";
import { PERMISSIONS } from "../../../../../auth";

const READ_PERMISSIONS = [
  PERMISSIONS.VIEW_STATUS,
  PERMISSIONS.VIEW_PROCESS,
  PERMISSIONS.VIEW_CONSUMPTION,
  PERMISSIONS.VIEW_CONFIGURATION,
  PERMISSIONS.VIEW_MESSAGING,
  PERMISSIONS.VIEW_MAINTENANCE
];

const WRITE_PERMISSIONS = [
  PERMISSIONS.CONFIGURATION,
  PERMISSIONS.MAINTENANCE,
  PERMISSIONS.EDIT
];

const EXECUTE_PERMISSIONS = [
  PERMISSIONS.QUERY,
  PERMISSIONS.REF_DATE_QUERY,
  PERMISSIONS.EXPORT
];

const isGroupChecked = (permissions, group) =>
  group.every(name => permissions[name]);

const isGroupIndeterminate = (permissions, group) =>
  group.some(name => permissions[name]) && !isGroupChecked(permissions, group);

const toggleGroup = (group, checked, setFieldValue) => {
  group.forEach(name => setFieldValue(`permissions.${name}`, checked));
};

const PermissionsForm = ({ username, values, onClose, onSubmit }) => {
  return (
    <Formik
      initialValues={values}
      onSubmit={(values, { setFieldError, setSubmitting }) =>
        onSubmit(values, { setFieldError, setSubmitting })
      }
    >
      {({ submitForm, values, setFieldValue, isSubmitting }) => {
        const isValid = () => {
          for (const permission in values.permissions) {
            if (values.permissions[permission]) {
              return true;
            }
          }

          return false;
        };

        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Section>
                  <SectionHeader>
                    <I18nText>devices.settings.access.sections.user</I18nText>
                  </SectionHeader>
                  {username}
                </Section>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Section>
                  <SectionHeader
                    checkbox
                    checked={isGroupChecked(
                      values.permissions,
                      READ_PERMISSIONS
                    )}
                    indeterminate={isGroupIndeterminate(
                      values.permissions,
                      READ_PERMISSIONS
                    )}
                    onCheckboxChange={e =>
                      toggleGroup(
                        READ_PERMISSIONS,
                        e.target.checked,
                        setFieldValue
                      )
                    }
                  >
                    <I18nText>access.permissions.aggregated.read</I18nText>
                  </SectionHeader>
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_STATUS}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_PROCESS}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_CONSUMPTION}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_CONFIGURATION}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_MESSAGING}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.VIEW_MAINTENANCE}
                    setFieldValue={setFieldValue}
                  />
                </Section>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Section>
                  <SectionHeader
                    checkbox
                    checked={isGroupChecked(
                      values.permissions,
                      WRITE_PERMISSIONS
                    )}
                    indeterminate={isGroupIndeterminate(
                      values.permissions,
                      WRITE_PERMISSIONS
                    )}
                    onCheckboxChange={e =>
                      toggleGroup(
                        WRITE_PERMISSIONS,
                        e.target.checked,
                        setFieldValue
                      )
                    }
                  >
                    <I18nText>access.permissions.aggregated.write</I18nText>
                  </SectionHeader>
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.CONFIGURATION}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.MAINTENANCE}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.EDIT}
                    setFieldValue={setFieldValue}
                  />
                </Section>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Section>
                  <SectionHeader
                    checkbox
                    checked={isGroupChecked(
                      values.permissions,
                      EXECUTE_PERMISSIONS
                    )}
                    indeterminate={isGroupIndeterminate(
                      values.permissions,
                      EXECUTE_PERMISSIONS
                    )}
                    onCheckboxChange={e =>
                      toggleGroup(
                        EXECUTE_PERMISSIONS,
                        e.target.checked,
                        setFieldValue
                      )
                    }
                  >
                    <I18nText>access.permissions.aggregated.execute</I18nText>
                  </SectionHeader>

                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.QUERY}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.REF_DATE_QUERY}
                    setFieldValue={setFieldValue}
                  />
                  <PermissionCheckbox
                    permissions={values.permissions}
                    name={PERMISSIONS.EXPORT}
                    setFieldValue={setFieldValue}
                  />
                </Section>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ButtonsWrapper
                  leftButton={
                    <CancelButton onClick={onClose} disabled={isSubmitting} />
                  }
                  rightButton={
                    <SaveButton
                      onClick={submitForm}
                      disabled={!isValid() || isSubmitting}
                    />
                  }
                />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PermissionsForm;
